import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Stack, Typography, Autocomplete, TextField, Button, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Snackbar, Alert, Chip, Divider, Tooltip,
  CircularProgress, MenuItem,} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import apiService from '../services/api.service';

interface CustomerOpt { cardCode: string; cardName: string; }
interface ItemOpt {
  itemCode: string; itemName: string; itemGroupCode?: number; itemGroupName?: string;
  inStock?: number; committed?: number; available?: number;
}
interface UomOption {
  entry: number; code: string; inventoryUnitsPer: number; isPricingUnit: boolean;
}
interface OrderLine {
  id: number;
  itemCode: string; itemDescription: string; itemGroupName?: string; warehouseCode: string;
  quantity: number; unitPrice: number;
  listPrice: number | null; floorPrice: number | null; maxDiscountPct: number;
  pricing?: boolean; // true while the async price lookup is in flight
  /** Units this item sells in. One entry means there is no choice to offer. */
  uoms?: UomOption[];
  /** Chosen unit. Undefined until the options load, or when there is only one. */
  uomEntry?: number;
}

const fmt = (n: number) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100; // mirror the server engine
const discountOf = (list: number | null, unit: number) => (list && list > 0 && unit < list ? ((list - unit) / list) * 100 : 0);

function SalesOrderCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editId = id ? Number(id) : undefined;
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || undefined, []);

  const [customer, setCustomer] = useState<CustomerOpt | null>(null);
  const [custOptions, setCustOptions] = useState<CustomerOpt[]>([]);
  const [custSearch, setCustSearch] = useState('');
  const [custLoading, setCustLoading] = useState(false);
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [itemOptions, setItemOptions] = useState<ItemOpt[]>([]);
  const [itemSearch, setItemSearch] = useState('');
  const [itemLoading, setItemLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItemOpt | null>(null);
  const [adding, setAdding] = useState(false);
  const lineSeqRef = useRef(0);   // unique id per line (matches async price fills)
  const addingRef = useRef(false); // synchronous re-entry guard against double-clicks
  const [remarks, setRemarks] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [credit, setCredit] = useState<any | null>(null);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const notify = useCallback((msg: string, sev: 'success' | 'error' = 'error') => setToast({ msg, sev }), []);
  const total = useMemo(() => round2(lines.reduce((s, l) => s + round2(l.quantity * l.unitPrice), 0)), [lines]);

  // Load an existing draft for editing.
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const res: any = await apiService.getSalesOrder(editId);
        const o = res.data;
        if (o.status !== 'Draft') { notify('Only draft orders can be edited'); navigate('/sales/orders'); return; }
        setCustomer({ cardCode: o.cardcode, cardName: o.cardname });
        setRemarks(o.remarks || '');
        setPoNumber(o.ponumber || '');
        const loaded: OrderLine[] = (o.lines || []).map((l: any, idx: number) => ({
          id: idx + 1,
          itemCode: l.itemcode, itemDescription: l.itemdescription || '', warehouseCode: l.warehousecode || '',
          quantity: Number(l.quantity), unitPrice: Number(l.unitprice),
          uomEntry: l.uomentry != null ? Number(l.uomentry) : undefined,
          listPrice: l.listprice != null ? Number(l.listprice) : null,
          floorPrice: l.floorprice != null ? Number(l.floorprice) : null, maxDiscountPct: 100,
        }));
        lineSeqRef.current = loaded.length;
        setLines(loaded);
        // Refresh live list/floor/max-discount so the client-side hints are accurate
        // (snapshots stored at draft time can be stale). Server stays authoritative.
        if (companyId) {
          const refreshed = await Promise.all(loaded.map(async (l) => {
            try {
              const r: any = await apiService.salesOrderPriceLookup(companyId, o.cardcode, l.itemCode);
              return { ...l, listPrice: r.data.listPrice, floorPrice: r.data.floorPrice, maxDiscountPct: Number(r.data.maxDiscountPct) || 0 };
            } catch { return l; }
          }));
          setLines(refreshed);
        }
      } catch (e: any) { notify(e.response?.data?.message || 'Failed to load draft'); }
    })();
  }, [editId, companyId, navigate, notify]);

  // Debounced customer search. Server-side; the Autocomplete must NOT re-filter
  // (filterOptions passthrough) — otherwise it hides server matches whose label
  // (`Name (Code)`, incl. Arabic + parentheses) doesn't substring-match the input.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setCustLoading(true);
    const t = setTimeout(async () => {
      try {
        const res: any = await apiService.getOrderCustomers(companyId, custSearch.trim() || undefined);
        if (!cancelled) setCustOptions(res.data || []);
      } catch { if (!cancelled) setCustOptions([]); }
      finally { if (!cancelled) setCustLoading(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [custSearch, companyId]);

  // Debounced item search (server-side, by code/description; also returns category).
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setItemLoading(true);
    const t = setTimeout(async () => {
      try {
        const res: any = await apiService.searchOrderItems(companyId, itemSearch.trim() || undefined);
        if (!cancelled) setItemOptions(res.data || []);
      } catch { if (!cancelled) setItemOptions([]); }
      finally { if (!cancelled) setItemLoading(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [itemSearch, companyId]);

  /**
   * Switch a line's unit. list/floor/unitPrice are all held in the CURRENTLY
   * selected unit, so moving between units rescales by the ratio of the two
   * factors. The server re-derives the factor from SAP on save and ignores
   * anything sent here — this is display arithmetic only.
   */
  const changeUom = (i: number, nextEntry: number) => {
    setLines((prev) => prev.map((l, idx) => {
      if (idx !== i || !l.uoms) return l;
      const from = l.uoms.find((u) => u.entry === l.uomEntry) ?? l.uoms.find((u) => u.isPricingUnit);
      const to = l.uoms.find((u) => u.entry === nextEntry);
      if (!from || !to || !(from.inventoryUnitsPer > 0)) return l;
      const ratio = to.inventoryUnitsPer / from.inventoryUnitsPer;
      const scale = (v: number | null) => (v == null ? null : round2(v * ratio));
      return {
        ...l,
        uomEntry: nextEntry,
        listPrice: scale(l.listPrice),
        floorPrice: scale(l.floorPrice),
        unitPrice: round2(l.unitPrice * ratio),
      };
    }));
  };

  const addLine = (item: ItemOpt | null) => {
    if (!companyId || !customer || !item) { notify('Pick a customer, then select an item'); return; }
    if (addingRef.current) return; // ignore rapid repeat clicks
    addingRef.current = true;
    setAdding(true);
    const id = ++lineSeqRef.current;
    const cardCode = customer.cardCode;
    // Append the line immediately (optimistic) so it shows up instantly and the
    // picker clears (disabling the button) — no blocking wait, no double-add.
    setLines((prev) => [...prev, {
      id, itemCode: item.itemCode, itemDescription: item.itemName || '', itemGroupName: item.itemGroupName,
      warehouseCode: '', quantity: 1, unitPrice: 0, listPrice: null, floorPrice: null, maxDiscountPct: 100, pricing: true,
    }]);
    setSelectedItem(null);
    setItemSearch('');
    // Fill in list/floor/unit once the (slower, SAP-backed) price lookup returns.
    void (async () => {
      try {
        // Price and units in parallel. The unit list never blocks the line:
        // if it fails the line simply has no dropdown and prices in the
        // item's own unit, exactly as before.
        const [res, uomRes] = await Promise.all([
          apiService.salesOrderPriceLookup(companyId, cardCode, item.itemCode),
          apiService.getSalesItemUoms(companyId, item.itemCode).catch(() => null),
        ]);
        const { listPrice, floorPrice, maxDiscountPct } = (res as any).data;
        const uoms: UomOption[] = (uomRes as any)?.data ?? [];
        const pricingUnit = uoms.find((u) => u.isPricingUnit) ?? uoms[0];
        // Default unit price to the floor (fall back to list, then 0).
        setLines((prev) => prev.map((l) => (l.id === id
          ? {
            ...l, listPrice, floorPrice, maxDiscountPct: Number(maxDiscountPct) || 0,
            unitPrice: floorPrice ?? listPrice ?? 0, pricing: false,
            uoms,
            // Prices arrive in the item's pricing unit, so that is the starting
            // selection; the user converts from there.
            uomEntry: pricingUnit?.entry,
          }
          : l)));
      } catch (e: any) {
        notify(e.response?.data?.message || 'Price lookup failed — enter the unit price manually');
        setLines((prev) => prev.map((l) => (l.id === id ? { ...l, pricing: false } : l)));
      } finally {
        addingRef.current = false;
        setAdding(false);
      }
    })();
  };

  const setLine = (i: number, patch: Partial<OrderLine>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const buildPayload = () => ({
    companyId, cardCode: customer?.cardCode, remarks, poNumber,
    lines: lines.map((l) => ({
      itemCode: l.itemCode, itemDescription: l.itemDescription || undefined,
      warehouseCode: l.warehouseCode || undefined, quantity: l.quantity, unitPrice: l.unitPrice,
      // Only the ENTRY is sent. The server resolves the conversion factor from
      // SAP itself — a client-supplied factor would scale the floor check.
      uomEntry: l.uomEntry,
    })),
  });

  const applyEvaluationFeedback = (data: any) => {
    setCredit(data?.evaluation?.credit ?? null);
  };

  const handleServerError = (e: any) => {
    if (e.response?.status === 422) {
      setErrors(e.response.data?.errors || ['Order validation failed']);
      applyEvaluationFeedback(e.response.data);
    } else {
      notify(e.response?.data?.message || 'Save failed');
    }
  };

  /** Save (create or update). Returns the order id, or null on failure. */
  const save = async (): Promise<number | null> => {
    if (!companyId || !customer || lines.length === 0) { notify('Pick a customer and add at least one line'); return null; }
    setBusy(true); setErrors([]);
    try {
      if (editId) {
        const res: any = await apiService.updateSalesOrder(editId, buildPayload());
        applyEvaluationFeedback(res.data);
        return editId;
      }
      const res: any = await apiService.createSalesOrder(buildPayload());
      applyEvaluationFeedback(res.data);
      return Number(res.data?.salesOrderId);
    } catch (e: any) {
      handleServerError(e);
      return null;
    } finally { setBusy(false); }
  };

  const onSaveDraft = async () => {
    const savedId = await save();
    if (savedId) { notify('Draft saved', 'success'); navigate('/sales/orders'); }
  };

  const onSubmit = async () => {
    const savedId = await save();
    if (!savedId) return;
    setBusy(true);
    try {
      await apiService.submitSalesOrder(savedId);
      notify('Submitted for approval', 'success');
      navigate('/sales/orders');
    } catch (e: any) {
      handleServerError(e);
    } finally { setBusy(false); }
  };

  if (!companyId) {
    return <Box sx={{ p: { xs: 2, md: 3 } }}><Alert severity="info">Select a company (top bar) to create an order.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" component="h1" gutterBottom>{editId ? 'Edit Sales Order' : 'Create Sales Order'}</Typography>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 260 }} size="small"
            options={customer && !custOptions.some((o) => o.cardCode === customer.cardCode) ? [customer, ...custOptions] : custOptions}
            value={customer}
            onChange={(_, v) => setCustomer(v)}
            onInputChange={(_, v, reason) => { if (reason === 'input') setCustSearch(v); }}
            filterOptions={(x) => x}
            getOptionLabel={(o) => `${o.cardName} (${o.cardCode})`}
            isOptionEqualToValue={(a, b) => a.cardCode === b.cardCode}
            loading={custLoading}
            disabled={!!editId}
            noOptionsText={custSearch ? 'No matching customers' : 'Type to search customers'}
            renderInput={(params) => (
              <TextField {...params} label="Customer" InputProps={{
                ...params.InputProps,
                endAdornment: (<>{custLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>),
              }} />
            )}
          />
          <TextField
            size="small" label="PO Number" value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            inputProps={{ maxLength: 100 }}
            helperText="Customer's own PO reference"
            sx={{ flex: 1 }}
          />
          <TextField size="small" label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} sx={{ flex: 1 }} />
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 280 }} size="small" options={itemOptions} value={selectedItem}
            onChange={(_, v) => setSelectedItem(v)}
            onInputChange={(_, v, reason) => { if (reason === 'input') setItemSearch(v); }}
            filterOptions={(x) => x}
            getOptionLabel={(o) => `${o.itemCode} — ${o.itemName}`}
            isOptionEqualToValue={(a, b) => a.itemCode === b.itemCode}
            loading={itemLoading}
            disabled={!customer}
            noOptionsText={!customer ? 'Pick a customer first' : itemSearch ? 'No matching items' : 'Type to search items'}
            renderOption={(props, o) => (
              <li {...props} key={o.itemCode}>
                <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                  <Typography variant="body2"><strong>{o.itemCode}</strong> — {o.itemName}</Typography>
                  {o.itemGroupName && <Typography variant="caption" color="text.secondary">{o.itemGroupName}</Typography>}
                  {o.available !== undefined && (
                    <Typography variant="caption" sx={{ color: (o.available ?? 0) > 0 ? 'success.main' : 'error.main' }}>
                      On hand {fmt(o.inStock ?? 0)} · Committed {fmt(o.committed ?? 0)} · Available {fmt(o.available)}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Search item (code / description)" InputProps={{
                ...params.InputProps,
                endAdornment: (<>{itemLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>),
              }} />
            )}
          />
          <Button variant="outlined" startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />} onClick={() => addLine(selectedItem)} disabled={!customer || !selectedItem || adding}>Add line</Button>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell><TableCell align="right">Qty</TableCell>
              <TableCell>UoM</TableCell>
              <TableCell align="right">List</TableCell><TableCell align="right">Floor</TableCell>
              <TableCell align="right">Unit Price</TableCell><TableCell align="right">Disc %</TableCell>
              <TableCell align="right">Line Total</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.length === 0 && <TableRow><TableCell colSpan={9}><Typography variant="caption" color="text.secondary">No lines yet.</Typography></TableCell></TableRow>}
            {lines.map((l, i) => {
              const disc = discountOf(l.listPrice, l.unitPrice);
              const belowFloor = l.floorPrice != null && l.unitPrice < l.floorPrice;
              const discExceeds = disc > l.maxDiscountPct + 1e-6;
              return (
                <TableRow key={l.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{l.itemCode}</Typography>
                    {l.itemDescription && <Typography variant="caption" color="text.secondary" display="block">{l.itemDescription}</Typography>}
                    {l.itemGroupName && <Chip size="small" variant="outlined" label={l.itemGroupName} sx={{ mt: 0.25, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }} />}
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={l.quantity} sx={{ width: 80 }}
                      onChange={(e) => setLine(i, { quantity: Number(e.target.value) })} inputProps={{ min: 0, style: { textAlign: 'right' } }} />
                  </TableCell>
                  <TableCell>
                    {/* Dropdown only when the item genuinely sells in more than
                        one unit — a single-option select on every line is noise. */}
                    {l.uoms && l.uoms.length > 1 ? (
                      <TextField
                        select size="small" value={l.uomEntry ?? ''} sx={{ minWidth: 88 }}
                        onChange={(e) => changeUom(i, Number(e.target.value))}
                        inputProps={{ 'aria-label': `Unit of measure for ${l.itemCode}` }}
                      >
                        {l.uoms.map((u) => <MenuItem key={u.entry} value={u.entry}>{u.code}</MenuItem>)}
                      </TextField>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {l.uoms?.[0]?.code || '—'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{l.pricing ? <CircularProgress size={12} /> : (l.listPrice != null ? fmt(l.listPrice) : '—')}</TableCell>
                  <TableCell align="right">{l.pricing ? <CircularProgress size={12} /> : (l.floorPrice != null ? fmt(l.floorPrice) : '—')}</TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={l.unitPrice} sx={{ width: 110 }}
                      error={belowFloor || discExceeds}
                      onChange={(e) => setLine(i, { unitPrice: Number(e.target.value) })} inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }} />
                  </TableCell>
                  <TableCell align="right">
                    {disc > 0 ? `${disc.toFixed(2)}%` : '—'}
                    {discExceeds && <Tooltip title={`Exceeds max ${l.maxDiscountPct}%`}><Chip size="small" color="warning" label="!" sx={{ ml: 0.5 }} /></Tooltip>}
                    {belowFloor && <Tooltip title="Below floor price"><Chip size="small" color="error" label="floor" sx={{ ml: 0.5 }} /></Tooltip>}
                  </TableCell>
                  <TableCell align="right">{fmt(l.quantity * l.unitPrice)}</TableCell>
                  <TableCell align="right"><IconButton size="small" color="error" onClick={() => removeLine(i)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </Box>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
          <Typography variant="h6">Total: {fmt(total)} {/* currency shown after save */}</Typography>
        </Stack>
      </Paper>

      {credit && credit.status !== 'OK' && (
        <Alert severity={credit.status === 'Blocked' ? 'error' : 'warning'} sx={{ mb: 2 }}>
          {credit.message || `Credit ${credit.status}`}
        </Alert>
      )}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Cannot proceed:</Typography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </Alert>
      )}

      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" startIcon={<SaveIcon />} onClick={() => void onSaveDraft()} disabled={busy}>Save Draft</Button>
        <Button variant="contained" startIcon={<SendIcon />} onClick={() => void onSubmit()} disabled={busy}>Submit for Approval</Button>
      </Stack>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export default SalesOrderCreate;
